import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT, Injectable, PLATFORM_ID, inject } from '@angular/core';
import { ScriptLoaderService } from 'widget';

import { IStoryblokBridge, IStoryblokBridgeStoryEvent } from './storyblok-bridge.types';

/**
 * Always load `-latest` rather than a pinned build: the bridge talks to the
 * Visual Editor over `postMessage`, so its version has to track the editor's.
 */
const BRIDGE_SCRIPT_URL = 'https://app.storyblok.com/f/storyblok-v2-latest.js';

/**
 * Marker class that activates the preview-only outline stylesheet
 * (`styles/_storyblok-preview.scss`).
 */
const PREVIEW_BODY_CLASS = 'storyblok-preview';

/**
 * Loads and owns the Storyblok Preview Bridge, which powers click-to-edit and
 * block highlighting inside the Storyblok Visual Editor.
 *
 * Only ever active when the backend reports `content.body.isInEditor`, so the
 * CDN script is never requested for real visitors.
 */
@Injectable({ providedIn: 'root' })
export class StoryblokBridgeService {
    private _scriptLoader = inject(ScriptLoaderService);
    private _document = inject<Document>(DOCUMENT);
    private _isBrowser = isPlatformBrowser(inject(PLATFORM_ID));

    private _initPromise?: Promise<void>;
    private _bridge?: IStoryblokBridge;

    /**
     * Idempotent: the caller runs on every page load, but the bridge installs
     * document-level listeners and a `postMessage` handshake with the editor,
     * so it must be constructed exactly once per document.
     *
     * Constructing once is safe because we annotate blocks with `data-blok-*`
     * attributes rather than HTML comments — the bridge resolves those on each
     * click, so a single instance keeps working after Angular re-renders the
     * section list.
     */
    init(): Promise<void> {
        if (!this._isBrowser) {
            return Promise.resolve();
        }

        this._initPromise ??= this._create();

        return this._initPromise;
    }

    /**
     * Nothing calls this today, and that is deliberate: preview mode cannot be
     * switched off mid-session (the editor iframe's URL is fixed), and tearing
     * the bridge down on navigation would break click-to-edit. Exposed for
     * symmetry and tests.
     */
    destroy(): void {
        this._bridge?.destroy();
        this._bridge = undefined;
        this._initPromise = undefined;
        this._document.body.classList.remove(PREVIEW_BODY_CLASS);
    }

    private async _create(): Promise<void> {
        this._document.body.classList.add(PREVIEW_BODY_CLASS);

        await this._scriptLoader.load(BRIDGE_SCRIPT_URL);

        const StoryblokBridge = this._document.defaultView?.StoryblokBridge;
        if (!StoryblokBridge) {
            throw new Error('StoryblokBridgeService: the bridge script loaded but exposed no StoryblokBridge global');
        }

        this._bridge = new StoryblokBridge({ preventClicks: true, initOnlyOnce: true });
        this._bridge.on(['change', 'published'], (payload) => this._onStoryChanged(payload));
    }

    /**
     * Reload rather than re-fetching through `PageDefaultService` — do not
     * "improve" this into `updatePage()`. That rebuilds the request from the
     * bare path and then `replaceState`s the browser URL without a query
     * string, which would strip the `_storyblok` / `_storyblok_tk[*]` params
     * that `StoryblokEditorHelper.IsInStoryblokEditor` validates. The next
     * response would come back as non-preview content with every
     * `storyblokEditable` missing.
     */
    private _onStoryChanged(payload: IStoryblokBridgeStoryEvent): void {
        // A renamed story makes the current URL stale; reloading it would 404.
        // The editor re-points the iframe itself in that case.
        if (payload.slugChanged) {
            return;
        }

        this._document.defaultView?.location.reload();
    }
}
